import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { pageViewApi, type PageViewSummaryResponse } from '../services/pageViewApi';
import { bookmarkApi, type PageBookmark } from '../services/bookmarkApi';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface PendingPageView {
  caseId: string;
  documentId: string;
  pageNumber: number;
}

interface DocumentProgressContextType {
  // Reading progress
  markPageVisited: (caseId: string, documentId: string, pageNumber: number) => void;
  viewSummary: PageViewSummaryResponse | null;
  refreshViewSummary: (caseId: string) => Promise<void>;
  getDocumentProgress: (documentId: string) => number;

  // Bookmarks
  bookmarks: PageBookmark[];
  refreshBookmarks: (caseId: string) => Promise<void>;
  addBookmark: (documentId: string, caseId: string, pageNumber: number, note?: string) => Promise<void>;
  removeBookmark: (documentId: string, bookmarkId: string) => Promise<void>;
  updateBookmarkNote: (documentId: string, bookmarkId: string, note: string) => Promise<void>;
  isPageBookmarked: (documentId: string, pageNumber: number) => boolean;
  getBookmarkForPage: (documentId: string, pageNumber: number) => PageBookmark | undefined;
}

// ─── Context ────────────────────────────────────────────────────────────────────

const DocumentProgressContext = createContext<DocumentProgressContextType | undefined>(undefined);

// ─── Hook: useDocumentProgress ──────────────────────────────────────────────────

export const useDocumentProgress = (): DocumentProgressContextType => {
  const context = useContext(DocumentProgressContext);
  if (context === undefined) {
    throw new Error('useDocumentProgress must be used within a DocumentProgressProvider');
  }
  return context;
};

// ─── Provider ───────────────────────────────────────────────────────────────────

interface DocumentProgressProviderProps {
  children: ReactNode;
}

export const DocumentProgressProvider: React.FC<DocumentProgressProviderProps> = ({ children }) => {
  const [viewSummary, setViewSummary] = useState<PageViewSummaryResponse | null>(null);
  const [bookmarks, setBookmarks] = useState<PageBookmark[]>([]);

  const pendingPageViews = useRef<PendingPageView[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Reading Progress ────────────────────────────────────────────────────────

  const refreshViewSummary = useCallback(async (caseId: string) => {
    const summary = await pageViewApi.getViewSummary(caseId);
    setViewSummary(summary);
  }, []);

  const flushPageViews = useCallback(() => {
    const views = [...pendingPageViews.current];
    pendingPageViews.current = [];

    if (views.length === 0) return;

    // Group by caseId + documentId for batch sending
    const grouped = new Map<string, { caseId: string; documentId: string; pageNumbers: number[] }>();
    for (const view of views) {
      const key = `${view.caseId}:${view.documentId}`;
      const existing = grouped.get(key);
      if (existing) {
        if (!existing.pageNumbers.includes(view.pageNumber)) {
          existing.pageNumbers.push(view.pageNumber);
        }
      } else {
        grouped.set(key, {
          caseId: view.caseId,
          documentId: view.documentId,
          pageNumbers: [view.pageNumber],
        });
      }
    }

    // Fire-and-forget each batch, then refresh summary
    for (const { caseId, documentId, pageNumbers } of grouped.values()) {
      pageViewApi.recordBatchPageViews(caseId, documentId, pageNumbers)
        .then(() => refreshViewSummary(caseId))
        .catch(() => { /* silent */ });
    }
  }, [refreshViewSummary]);

  const markPageVisited = useCallback((caseId: string, documentId: string, pageNumber: number) => {
    pendingPageViews.current.push({ caseId, documentId, pageNumber });

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      flushPageViews();
    }, 2000);
  }, [flushPageViews]);

  const getDocumentProgress = useCallback((documentId: string): number => {
    if (!viewSummary) return 0;
    const doc = viewSummary.documents.find((d) => d.documentId === documentId);
    return doc?.viewedPages ?? 0;
  }, [viewSummary]);

  // ── Bookmarks ───────────────────────────────────────────────────────────────

  const refreshBookmarks = useCallback(async (caseId: string) => {
    const result = await bookmarkApi.getBookmarksForCase(caseId);
    setBookmarks(result);
  }, []);

  const addBookmark = useCallback(async (documentId: string, caseId: string, pageNumber: number, note?: string) => {
    const created = await bookmarkApi.createBookmark(documentId, { caseId, pageNumber, note });
    setBookmarks((prev) => [...prev, created]);
  }, []);

  const removeBookmark = useCallback(async (documentId: string, bookmarkId: string) => {
    // Optimistic removal
    setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkId));
    try {
      await bookmarkApi.deleteBookmark(documentId, bookmarkId);
    } catch (err) {
      // Revert on failure by re-fetching
      const failed = bookmarks.find((b) => b.id === bookmarkId);
      if (failed) {
        setBookmarks((prev) => [...prev, failed]);
      }
      throw err;
    }
  }, [bookmarks]);

  const updateBookmarkNote = useCallback(async (documentId: string, bookmarkId: string, note: string) => {
    const updated = await bookmarkApi.updateBookmark(documentId, bookmarkId, { note });
    setBookmarks((prev) => prev.map((b) => (b.id === bookmarkId ? updated : b)));
  }, []);

  const isPageBookmarked = useCallback((documentId: string, pageNumber: number): boolean => {
    return bookmarks.some((b) => b.documentId === documentId && b.pageNumber === pageNumber);
  }, [bookmarks]);

  const getBookmarkForPage = useCallback((documentId: string, pageNumber: number): PageBookmark | undefined => {
    return bookmarks.find((b) => b.documentId === documentId && b.pageNumber === pageNumber);
  }, [bookmarks]);

  return (
    <DocumentProgressContext.Provider
      value={{
        markPageVisited,
        viewSummary,
        refreshViewSummary,
        getDocumentProgress,
        bookmarks,
        refreshBookmarks,
        addBookmark,
        removeBookmark,
        updateBookmarkNote,
        isPageBookmarked,
        getBookmarkForPage,
      }}
    >
      {children}
    </DocumentProgressContext.Provider>
  );
};
